import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { IDocumentAnnotation } from '@apis/documentAnnotations';
import { useAuth } from '@contexts/useAuth';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { useFolders } from '@components/Pages/DocumentsPage/hooks/useFolders';
import { useDeleteAnnotation, useDocumentAnnotations } from '@components/Pages/DocumentsPage/hooks/useDocumentAnnotations';
import { AnnotationsBody } from './AnnotationsBody/AnnotationsBody';
import { DraftCard } from './AnnotationsSidebar/DraftCard/DraftCard';
import type { IHoveredMark } from './AnnotationsBody/AnnotationCanvas/AnnotationCanvas';
import { AnnotationsHeader } from './AnnotationsHeader/AnnotationsHeader';
import { AnnotationsSidebar } from './AnnotationsSidebar/AnnotationsSidebar';
import { DocumentContent } from './DocumentContent/DocumentContent';
import { AttributionChip } from './AttributionChip/AttributionChip';
import type { IAnnotationsNavState } from './annotations.url';
import { useAnnotationDownloads } from './hooks/useAnnotationDownloads';
import { useAnnotationDraft } from './hooks/useAnnotationDraft';
import { useDocumentContent } from './hooks/useDocumentContent';
import { usePaintedMarks, useVisibleItemsByPage } from './hooks/usePaintedMarks';
import { useVisibleAnnotations } from './hooks/useVisibleAnnotations';
import styles from './AnnotationsPage.module.css';

/** "root:path/name" → "name": the fallback for a cold deep link. */
function fileNameOf(fileId: string): string {
  return fileId.slice(fileId.lastIndexOf('/') + 1);
}

/**
 * The document viewer, and the surface annotations live on: the file with the
 * checked annotations over it, a list of who drew what, two downloads, and
 * drawing as a mode of the same page. The URL is the state, so it is shareable
 * and survives a reload.
 */
export function AnnotationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { siteId = '' } = useParams<{ siteId: string }>();
  const [params] = useSearchParams();
  const fileId = params.get('file') ?? '';
  const versionParam = Number(params.get('v'));
  const version = Number.isFinite(versionParam) && versionParam > 0 ? versionParam : null;

  const tz = useSelector(selectSiteTz) ?? null;
  const { canApprove } = useFolders(siteId);
  const { user } = useAuth();

  // A rename leaves the physical name behind, so callers pass the display one.
  const navState = useLocation().state as IAnnotationsNavState | null;
  const docName = navState?.docName ?? fileNameOf(fileId);
  const viewerRef = useRef<HTMLDivElement>(null);
  
  // The ref comes out on its own — spreading it would be a read during render.
  const { canvasRef, ...doc } = useDocumentContent(siteId, fileId, docName, viewerRef);

  const { data: annotations = [], isPending, isLoadingError, isFetching, refetch } = useDocumentAnnotations(siteId, fileId, !!fileId);
  const { visible, isVisible, toggle } = useVisibleAnnotations(annotations);
  const painted = usePaintedMarks(visible, doc.page);
  const itemsByPage = useVisibleItemsByPage(visible);

  // The document and its marks appear together, and drawing waits for both.
  const ready = doc.contentReady && !isPending;
  // Which of the three it waits on, so the disabled button can say.
  const blockedBy = doc.failed ? 'failed'
    : !doc.contentReady ? 'document'
      : isPending ? 'annotations'
        : isLoadingError ? 'annotationsFailed' : null;
  const remove = useDeleteAnnotation(siteId, fileId);
  const [hovered, setHovered] = useState<IHoveredMark | null>(null);
  
  // A draft outlives no session: ending one empties it.
  const [drawing, setDrawing] = useState(false);

  const [confirmingDiscard, setConfirmingDiscard] = useState<'stop' | 'leave' | null>(null);
  // The annotation awaiting a confirmed delete — a delete can't be undone.
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const draft = useAnnotationDraft(siteId, fileId, doc.page, () => setDrawing(false), doc.setPage);
  const download = useAnnotationDownloads({
    siteId, fileId, docName,
    isPdf: doc.isPdf,
    blob: doc.blob,
    itemsByPage,
    page: doc.page,
    downloadName: navState?.downloadName,
  });

  // Words still in the text box land on the page they were typed on, then the page turns.
  function turnPage(page: number) {
    draft.markup.settleText();
    doc.setPage(page);
  }

  function stopDrawing(then: 'stop' | 'leave' | null) {
    draft.reset();
    setConfirmingDiscard(null);
    setDrawing(false);
    if (then === 'leave') navigate(-1);
  }

  const hoveredAnnotation: IDocumentAnnotation | null =
    hovered ? (annotations.find((a) => a.id === hovered.annotationId) ?? null) : null;

  /** Unseen marks are the one loss the page cannot undo, so leaving asks. */
  function requestDiscard(then: 'stop' | 'leave') {
    if (draft.dirty) setConfirmingDiscard(then);
    else stopDrawing(then);
  }

  // A hand-edited link with no file has nothing to load.
  if (!fileId) {
    return <DataUnavailable message={t('documents.annotations.noFile')} />;
  }

  return (
    <div className={styles.page}>
      <AnnotationsHeader
        docName={docName}
        version={version}
        onBack={() => requestDiscard('leave')}
        backDisabled={draft.saving}
        onAnnotate={drawing ? undefined : () => setDrawing(true)}
        annotateBlockedBy={blockedBy}
      />

      <div className={styles.body}>
        <AnnotationsSidebar
          draftCard={drawing ? <DraftCard draft={draft} onDiscard={() => requestDiscard('stop')} /> : null}
          annotations={annotations}
          loading={isPending || (isLoadingError && isFetching)} // a Retry in flight reads as loading
          loadFailed={isLoadingError}
          onRetryLoad={() => void refetch()}
          tz={tz}
          viewerUserId={user?.id ?? null}
          isAdmin={canApprove}
          isVisible={isVisible}
          onToggle={toggle}
          // A new delete starts clean — an earlier refusal is not this one's.
          onDelete={(id) => { remove.reset(); setDeletingId(id); }}
          deleteError={remove.isError ? t('documents.annotations.deleteFailed') : null}
          onDownloadWithAnnotations={() => void download.withAnnotations()}
          onDownloadOriginal={() => void download.original()}
          downloading={download.downloading}
          blockedBy={blockedBy}
          downloadError={download.failure && t(`documents.markup.${download.failure === 'encrypted' ? 'encryptedError' : 'downloadError'}`)}
        />

        <AnnotationsBody
          ref={viewerRef}
          content={
            <DocumentContent
              isPdf={doc.isPdf}
              shown={ready}
              canvasRef={canvasRef}
              imageUrl={doc.imageUrl}
              docName={docName}
              imageZoomStyle={doc.imageZoomStyle}
              onImageLoad={doc.onImageLoad}
            />
          }
          contentKey={doc.contentKey}
          contentReady={ready}
          failed={!!doc.failed}
          painted={painted}
          onHover={setHovered}
          draft={drawing ? draft : null}
          zoom={doc.zoom}
          onZoomChange={doc.setZoomPct}
          onZoomDelta={doc.applyZoom}
          page={doc.page}
          pageCount={doc.pageCount}
          onPageChange={turnPage}
          shown={visible.length}
          total={annotations.length}
        />
      </div>

      {hoveredAnnotation && hovered && (
        <AttributionChip annotation={hoveredAnnotation} tz={tz} x={hovered.x} y={hovered.y} />
      )}

      <DiscardModal
        open={deletingId !== null}
        title={t('documents.annotations.deleteTitle')}
        description={t('documents.annotations.deleteBody')}
        confirmLabel={t('documents.annotations.deleteConfirm')}
        onConfirm={() => { remove.mutate(deletingId!); setDeletingId(null); }}
        onCancel={() => setDeletingId(null)}
      />

      <DiscardModal
        open={confirmingDiscard !== null}
        title={t('documents.annotations.discardTitle')}
        description={t('documents.annotations.discardBody')}
        confirmLabel={t('documents.annotations.discardConfirm')}
        onConfirm={() => stopDrawing(confirmingDiscard)}
        onCancel={() => setConfirmingDiscard(null)}
      />
    </div>
  );
}
